import Spinner from '@components/spinner/Spinner';
import { Close, DragIndicator, MicOffOutlined, Videocam } from '@mui/icons-material';
import { Alert, Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { fetchRecordingPlaybackUrl, recordingApiBase } from '@services/interview';
import { mediaType } from '@utils/constants';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import ReactPlayer from 'react-player';

// forwardRef so the parent's playerRef points at the underlying <audio>
// element / ReactPlayer instance. Destructuring `ref` directly from props
// (the React 19 "ref as prop" shorthand) doesn't reliably reach the inner
// element here because this project's Vite pipeline runs
// babel-plugin-react-compiler — the per-turn play buttons in
// TranscriptDetail call playerRef.current.currentTime / .seekTo, which
// silently no-op when the ref never attaches. Every other ref'd
// component in this codebase uses forwardRef for the same reason.
export const RecordingFile = forwardRef(function RecordingFile(
  {
    playing,
    setPlayedSeconds,
    mediaData,
    isLoadingVideo,
    setPlaying,
    studyId,
    transcriptId,
    useAspectRatio = true,
    aspectRatio = '16/9',
  },
  ref,
) {
  const { ref: viewRef, inView } = useInView({ threshold: 0.1 });

  const recordingSources = mediaData?.recording_sources || [];
  const showParts = recordingSources.length > 1;
  const defaultPartIndex =
    showParts && recordingSources.some((part) => part.source === 'merged')
      ? recordingSources.findIndex((part) => part.source === 'merged')
      : 0;

  const [activePartIndex, setActivePartIndex] = useState(
    defaultPartIndex >= 0 ? defaultPartIndex : 0,
  );
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [playbackError, setPlaybackError] = useState(null);
  const [isLoadingPlayback, setIsLoadingPlayback] = useState(false);

  const activeSource = showParts
    ? recordingSources[activePartIndex]?.source
    : recordingSources[0]?.source || 'primary';

  // Mint a short-lived auth-checked playback URL whenever the source changes.
  useEffect(() => {
    let cancelled = false;

    if (!studyId || !transcriptId || !mediaData?.has_recording || !activeSource) {
      setPlaybackUrl(null);
      setPlaybackError(null);
      return undefined;
    }

    setIsLoadingPlayback(true);
    setPlaybackError(null);
    setPlaybackUrl(null);

    fetchRecordingPlaybackUrl({ studyId, transcriptId, source: activeSource })
      .then((data) => {
        if (cancelled) return;
        let url = `${recordingApiBase}${data.playback_url}`;
        // Cross-origin (staging/prod): <video> won't send the HttpOnly cookie.
        if (recordingApiBase && data.playback_token) {
          url += `&pt=${encodeURIComponent(data.playback_token)}`;
        }
        setPlaybackUrl(url);
      })
      .catch((err) => {
        console.error('[RecordingFile] Failed to load playback URL:', err);
        if (!cancelled) {
          setPlaybackError('Unable to load the recording. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPlayback(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studyId, transcriptId, mediaData?.has_recording, activeSource]);

  // Sticky video state
  const [isStickyClosed, setIsStickyClosed] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 }); // bottom-right offset
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // Reset sticky closed state when transcript changes
  useEffect(() => {
    setIsStickyClosed(false);
    setPosition({ x: 20, y: 20 });
    setActivePartIndex(defaultPartIndex >= 0 ? defaultPartIndex : 0);
  }, [transcriptId, defaultPartIndex]);

  // Drag handlers
  const handleMouseDown = useCallback(
    (e) => {
      if (e.target.closest('.drag-handle')) {
        setIsDragging(true);
        dragStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          posX: position.x,
          posY: position.y,
        };
        e.preventDefault();
      }
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const deltaX = dragStartRef.current.x - e.clientX;
      const deltaY = dragStartRef.current.y - e.clientY;

      const newX = Math.max(
        0,
        Math.min(window.innerWidth - 340, dragStartRef.current.posX + deltaX),
      );
      const newY = Math.max(
        0,
        Math.min(window.innerHeight - 200, dragStartRef.current.posY + deltaY),
      );

      setPosition({ x: newX, y: newY });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const showSticky = !inView && !isStickyClosed;
  const isPlayerLoading = isLoadingVideo || isLoadingPlayback;

  if (isPlayerLoading && !playbackUrl) return <Spinner />;

  // No recording found - subtle inline message
  if (!mediaData?.has_recording || mediaData?.media_type === 'text') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: 2,
          mt: 1,
          borderRadius: 1,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <MicOffOutlined sx={{ fontSize: 20, color: 'grey.500' }} />
        <Typography variant="body2" color="text.secondary">
          No audio or video recording associated with this transcript.
        </Typography>
      </Box>
    );
  }

  if (playbackError) {
    return (
      <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
        {playbackError}
      </Alert>
    );
  }

  // Check if video is being processed
  const interviewStatus = (mediaData?.interview_status || '').toLowerCase();
  const isActiveInterview = interviewStatus === 'active';
  const isProcessing = mediaData?.duration_fixing === true;
  const needsProcessing = mediaData?.duration_fixed === false && !isProcessing;
  const showActiveRecordingAlert = isActiveInterview && needsProcessing;
  const showDurationFixAlert = !showActiveRecordingAlert && (isProcessing || needsProcessing);

  return (
    <>
      {/* Video processing alert */}
      {showActiveRecordingAlert && (
        <Alert severity="info" sx={{ mb: 2, mt: 1 }} icon={<Videocam />}>
          Recording is still in progress. Seeking optimization will start after the interview ends.
        </Alert>
      )}

      {showDurationFixAlert && (
        <Alert severity="info" sx={{ mb: 2, mt: 1 }} icon={<Videocam />}>
          {isProcessing
            ? 'Video is being optimized for playback. Seeking may not work until processing completes.'
            : 'Video duration metadata is being fixed. This may take a minute.'}
        </Alert>
      )}

      {/* Show video button when sticky is closed */}
      {!inView && isStickyClosed && (
        <Tooltip title="Show video player">
          <IconButton
            onClick={() => setIsStickyClosed(false)}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 999999999,
              backgroundColor: 'primary.main',
              color: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <Videocam />
          </IconButton>
        </Tooltip>
      )}

      {/* Part selector for multi-session recordings */}
      {showParts && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {recordingSources.map((part, index) => (
            <Chip
              key={part.source}
              label={part.label}
              size="small"
              variant={activePartIndex === index ? 'filled' : 'outlined'}
              color={
                part.source === 'merged'
                  ? activePartIndex === index
                    ? 'success'
                    : 'default'
                  : activePartIndex === index
                    ? 'primary'
                    : 'default'
              }
              onClick={() => {
                setActivePartIndex(index);
                setPlaying(false);
                setPlayedSeconds(0);
              }}
              sx={{ fontSize: '0.8rem' }}
            />
          ))}
        </Box>
      )}

      <div ref={viewRef}>
        {mediaData.media_type === mediaType.audio ? (
          // Audio player
          <audio
            ref={ref}
            src={playbackUrl || undefined}
            controls
            style={{
              width: showSticky ? 400 : '100%',
              background: '#f2f2f2',
              borderRadius: 8,
              boxShadow: showSticky ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
              position: showSticky ? 'fixed' : 'static',
              bottom: showSticky ? position.y : undefined,
              right: showSticky ? position.x : undefined,
              zIndex: showSticky ? 999999999 : 1,
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onTimeUpdate={(e) => setPlayedSeconds(e.currentTarget.currentTime)}
          >
            <track kind="captions" src="" srcLang="en" label="English" default />
          </audio>
        ) : (
          // Single video player that repositions between inline and PiP
          <Box
            sx={
              showSticky
                ? {
                    position: 'fixed',
                    bottom: position.y,
                    right: position.x,
                    zIndex: 999999999,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    cursor: isDragging ? 'grabbing' : 'default',
                    userSelect: 'none',
                    width: 320,
                    height: 180,
                  }
                : useAspectRatio
                  ? {
                      position: 'relative',
                      width: '100%',
                      aspectRatio: aspectRatio,
                      backgroundColor: '#000',
                    }
                  : {
                      width: '100%',
                      height: 300,
                      backgroundColor: '#000',
                    }
            }
            onMouseDown={showSticky ? handleMouseDown : undefined}
          >
            {/* PiP control bar */}
            {showSticky && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '4px 8px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                  zIndex: 10,
                }}
              >
                <Tooltip title="Drag to move">
                  <IconButton
                    className="drag-handle"
                    size="small"
                    sx={{
                      color: 'white',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <DragIndicator fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close mini player">
                  <IconButton
                    onClick={() => setIsStickyClosed(true)}
                    size="small"
                    sx={{
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <ReactPlayer
              ref={ref}
              url={playbackUrl || undefined}
              controls
              playing={playing}
              onProgress={({ playedSeconds }) => setPlayedSeconds(playedSeconds)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              width="100%"
              height="100%"
              style={{
                position: showSticky ? 'static' : 'absolute',
                top: 0,
                left: 0,
                background: '#000',
              }}
            />
          </Box>
        )}
      </div>
    </>
  );
});
